import AccordionMui from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { GradientTypography } from 'src/components/gradients/GradientTypography';
import { Competition } from 'functions/src/types/firestore/Game/Competition';
import { LiveBadge } from '../LiveBadge';
import { Tournament } from 'functions/src/types/firestore/Game/Tournament';
import { Optional } from 'utility-types';
import { useAccordionizeCompetitions } from 'src/hooks/useAccordionizeCompetitions';

export type CompetitionBody = Pick<
  Competition<Date>,
  'title' | 'date' | 'endDate' | 'prize' | 'id'
> & {
  Details: JSX.Element | JSX.Element[];
} & Optional<Pick<Tournament<Date>, 'phase'>>;

export type CompetitionAccordionProps = {
  competitions: CompetitionBody[];
  chosenTournamentId?: string;
};

export const CompetitionAccordion = ({
  competitions,
  chosenTournamentId,
}: CompetitionAccordionProps) => {
  const { chosen, competitionsInternal } = useAccordionizeCompetitions({
    competitions,
    chosenTournamentId,
  });
  const [chosenExpanded, setChosenExpanded] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);

  const handleChange = useCallback(
    (panel: string) => {
      return (_event: SyntheticEvent, isExpanded: boolean) => {
        if (isExpanded && !expanded.includes(panel)) {
          setExpanded([panel, ...expanded]);
        } else if (!isExpanded && expanded.includes(panel)) {
          setExpanded(
            expanded.filter((expandedPanel) => {
              return expandedPanel !== panel;
            }),
          );
        }
      };
    },
    [expanded],
  );
  useEffect(() => {
    if (
      competitionsInternal.length > 0 &&
      chosen !== undefined &&
      !chosenExpanded
    ) {
      handleChange(competitionsInternal[Number(chosen)].summary)(
        undefined as unknown as SyntheticEvent,
        true,
      );
      setChosenExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, competitionsInternal]);

  const chosenRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (
      chosen !== undefined &&
      competitionsInternal.length > 0 &&
      chosenRef.current
    ) {
      chosenRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [chosen, competitionsInternal.length]);

  return (
    <>
      {competitionsInternal.map(({ summary, isLive, Details }, i) => {
        return (
          <AccordionMui
            ref={chosen === i ? chosenRef : undefined}
            key={`accordion-${summary}`}
            disableGutters
            sx={{ width: '100%', border: 'none' }}
            expanded={expanded.includes(summary)}
            onChange={handleChange(summary)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
              sx={{
                backgroundColor: 'background.elevation.8',
                borderBottom: 'none',
              }}
            >
              {isLive && (
                <Box sx={{ alignSelf: 'center', pr: 3 }}>
                  <LiveBadge />
                </Box>
              )}

              <GradientTypography
                variant={'h5'}
                gradientColor="warning.vertical"
                sx={{
                  fontSize: '22px',
                  textTransform: 'uppercase',
                  alignSelf: 'center',
                }}
              >
                {summary}
              </GradientTypography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                background: 'transparent',
                '& .MuiPaper-root': {
                  background: 'transparent',
                  boxShadow: 'none',
                },
              }}
            >
              {Details}
            </AccordionDetails>
          </AccordionMui>
        );
      })}
    </>
  );
};
